import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { userProfileService } from '@/services/userProfileService'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { useAuth } from '@/contexts/AuthContext'
import { UserProfile } from '@/types'
import {
  User,
  Building2,
  Hash,
  Phone,
  Mail,
  Save,
  AlertCircle,
  CheckCircle2,
  Loader2,
  MapPin,
  Home
} from 'lucide-react'
import {
  PageContainer,
  PageHeader,
  DataCard,
  LoadingState
} from '@/components/common'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { FeatureFlag } from '@/constants/featureFlags'
import { PayoutCompaniesTab } from '@/components/settings/PayoutCompaniesTab'

export function UserProfilePage() {
  const { currentUser, userEmail } = useAuth()
  const multiCompanyEnabled = useFeatureFlag(FeatureFlag.MULTI_COMPANY)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [formData, setFormData] = useState({
    displayName: '',
    companyName: '',
    organizationNumber: '',
    phoneNumber: '',
    address: '',
    city: '',
    postalCode: ''
  })
  const [originalFormData, setOriginalFormData] = useState({
    displayName: '',
    companyName: '',
    organizationNumber: '',
    phoneNumber: '',
    address: '',
    city: '',
    postalCode: ''
  })

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData)
  useUnsavedChanges(hasChanges)


  // Load user profile
  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) return

      try {
        setLoading(true)
        const userProfile = await userProfileService.getUserProfile(currentUser.uid)

        if (userProfile) {
          setProfile(userProfile)
          const loaded = {
            displayName: userProfile.displayName,
            companyName: userProfile.companyName || '',
            organizationNumber: userProfile.organizationNumber || '',
            phoneNumber: userProfile.phoneNumber || '',
            address: userProfile.address || '',
            city: userProfile.city || '',
            postalCode: userProfile.postalCode || ''
          }
          setFormData(loaded)
          setOriginalFormData(loaded)
        } else {
          // Create profile if it doesn't exist
          await userProfileService.createUserProfile(currentUser)
          // Reload after creation
          loadProfile()
        }
      } catch (err: any) {
        // Profile loading error
        if (err.code === 'firestore/permission-denied') {
          setError('Firestore-databasen är inte aktiverad. Vänligen kontakta administratören.')
        } else if (err.code === 'firestore/unavailable') {
          setError('Databasen är för närvarande otillgänglig. Försök igen senare.')
        } else {
          setError(err.message || 'Kunde inte ladda användarprofilen')
        }
      } finally {
        setLoading(false)
      }
    }

    loadProfile()
  }, [currentUser])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (error || success) {
      setError('')
      setSuccess(false)
    }
  }


  const validateForm = (): boolean => {
    if (!formData.displayName.trim()) {
      setError('Namn är obligatoriskt')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm() || !currentUser || !profile) return

    setSaving(true)
    setError('')
    setSuccess(false)

    try {
      const updatedProfile: Partial<UserProfile> = {
        displayName: formData.displayName.trim(),
        companyName: formData.companyName.trim() || undefined,
        organizationNumber: formData.organizationNumber.trim() || undefined,
        phoneNumber: formData.phoneNumber.trim() || undefined,
        address: formData.address.trim() || undefined,
        city: formData.city.trim() || undefined,
        postalCode: formData.postalCode.trim() || undefined,
        profileCompleted: true
      }

      await userProfileService.updateUserProfile(currentUser.uid, updatedProfile)
      setSuccess(true)
      setOriginalFormData({ ...formData })

      // Update local profile state
      setProfile({ ...profile, ...updatedProfile })

      // Auto-hide success message
      setTimeout(() => setSuccess(false), 3000)
    } catch (err: any) {
      // Profile save error
      if (err.code === 'firestore/permission-denied') {
        setError('Firestore-databasen är inte aktiverad. Vänligen kontakta administratören.')
      } else if (err.code === 'firestore/unavailable') {
        setError('Databasen är för närvarande otillgänglig. Försök igen senare.')
      } else {
        setError(err.message || 'Kunde inte spara ändringarna i användarprofilen')
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingState
          message="Laddar profil..."
          icon={<Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />}
        />
      </PageContainer>
    )
  }

  const profileContent = (
    <>
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <div className="text-red-800 font-medium">{error}</div>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <div className="text-green-800 font-medium">
            Profilen har sparats framgångsrikt!
          </div>
        </Alert>
      )}

      {/* Personal Information */}
      <DataCard title="Personlig Information">
        <div className="space-y-4">
          {/* Email (read-only) */}
          <div className="space-y-2">
            <Label htmlFor="email">E-postadress</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={userEmail}
                disabled
                className="pl-10 bg-muted"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              E-postadressen kan inte ändras
            </p>
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="displayName">
              Namn <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="displayName"
                name="displayName"
                type="text"
                placeholder="Ditt för- och efternamn"
                value={formData.displayName}
                onChange={handleInputChange}
                className="pl-10"
                disabled={saving}
              />
            </div>
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="phoneNumber">Telefonnummer</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                placeholder="070-123 45 67"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="pl-10"
                disabled={saving}
              />
            </div>
          </div>
        </div>
      </DataCard>

      {/* Company Information */}
      <DataCard title="Företagsinformation">
        <div className="space-y-4">
          {/* Company Name */}
          <div className="space-y-2">
            <Label htmlFor="companyName">Företagsnamn</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="companyName"
                name="companyName"
                type="text"
                placeholder="Ditt företags namn"
                value={formData.companyName}
                onChange={handleInputChange}
                className="pl-10"
                disabled={saving}
              />
            </div>
          </div>

          {/* Organization Number */}
          <div className="space-y-2">
            <Label htmlFor="organizationNumber">Organisationsnummer</Label>
            <div className="relative">
              <Hash className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="organizationNumber"
                name="organizationNumber"
                type="text"
                placeholder="XXXXXXXX-XXXX"
                value={formData.organizationNumber}
                onChange={handleInputChange}
                className="pl-10"
                disabled={saving}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Format: 12345678-9012
            </p>
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address">Adress</Label>
            <div className="relative">
              <Home className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="address"
                name="address"
                type="text"
                placeholder="Gatuadress och nummer"
                value={formData.address}
                onChange={handleInputChange}
                className="pl-10"
                disabled={saving}
              />
            </div>
          </div>

          {/* City and Postal Code */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">Ort</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="city"
                  name="city"
                  type="text"
                  placeholder="Stad/Ort"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="pl-10"
                  disabled={saving}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="postalCode">Postnummer</Label>
              <Input
                id="postalCode"
                name="postalCode"
                type="text"
                placeholder="123 45"
                value={formData.postalCode}
                onChange={handleInputChange}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      </DataCard>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="min-w-[120px]"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Sparar...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Spara ändringar
            </>
          )}
        </Button>
      </div>
    </>
  )

  if (!multiCompanyEnabled) {
    return (
      <PageContainer>
        <PageHeader
          title="Min Profil"
          subtitle="Hantera din användarprofil, företagsinformation och integritetsinställningar"
        />
        {profileContent}
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title="Min Profil"
        subtitle="Hantera din användarprofil, företagsinformation och integritetsinställningar"
      />
      <Tabs defaultValue="profil">
        <TabsList>
          <TabsTrigger value="profil">Profil</TabsTrigger>
          <TabsTrigger value="bolag">Bolag</TabsTrigger>
        </TabsList>

        <TabsContent value="profil" className="space-y-4 mt-4">
          {profileContent}
        </TabsContent>

        <TabsContent value="bolag" className="mt-4">
          <PayoutCompaniesTab />
        </TabsContent>
      </Tabs>
    </PageContainer>
  )
}
